import {
  Box,
  TextField,
  MenuItem,
  Typography,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  FormControlLabel,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import { inputWidth } from '../../add-visit-flow'
import type {
  PortInfoOperation,
  PortInfoCargoType,
  PortInfoIspsSecurityLevel,
  PageData,
} from '../../../../types'
import { REMARK_FIELDS } from '../../../../types'
import { PortAutocomplete } from '../page-one/port-autocomplete'
import { PortInfoFieldAutocomplete } from '../page-one/port-info-field-autocomplete'
import { VesselAutocomplete } from '../page-one/vessel-autocomplete'
import { useFormHandlers } from '../../../../hooks/use-form-handlers'
import { useDateFormat } from '~hooks/use-date-format'
import { CalendarIcon, ClipIcon } from 'src/components/icons'
import { COLORS } from '~theme/colors'
import { RemarkFieldWithAttach } from './remark-field-with-attach'
import { usePostPortInfoFile } from '~hooks/queries/port-info/use-post-port-info-file'
import { useDeletePortInfoFile } from '~hooks/queries/port-info/use-delete-port-info-file'
import { useAnimatedAlert } from '~components/molecules/animated-alert/animated-alert-provider'
import {
  sectionBoxSx,
  fieldsContainerSx,
  remarksGridSx,
  MAX_FILE_SIZE_LABEL,
} from '../../../../styles'
import { getFilesForField } from '../../../../utils/form-utils'
import { FALLBACK_STRING_NA } from '~constants/global-strings'
import { pools } from '~types/vessel.types'

const operationOptions: PortInfoOperation[] = [
  'LOAD',
  'DISCHARGE',
  'LAYBY',
  'CLEANING',
  'STS',
  'OTHER',
]

const cargoTypeOptions: PortInfoCargoType[] = ['CHEMICALS', 'CPP', 'DPP', 'GAS']

const ispsSecurityLevelOptions: PortInfoIspsSecurityLevel[] = ['1', '2', '3']

const hideSpinnersSx = {
  '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
    WebkitAppearance: 'none',
    margin: 0,
  },
  '& input[type=number]': {
    MozAppearance: 'textfield',
  },
}

export function PageTwo({
  formData = {},
  updateFormData,
  portInfoId,
  files = [],
  missingFields = [],
}: PageData) {
  const numberInputSx = { width: inputWidth, ...hideSpinnersSx }
  const dateFormat = useDateFormat('numeric')
  const { onChangeHandler, handleNumberChange, handleVesselChange } =
    useFormHandlers(formData, updateFormData)
  const { setStatus } = useAnimatedAlert()
  const { mutate: uploadFile, isPending: isUploading } = usePostPortInfoFile()
  const { mutate: deleteFile } = useDeletePortInfoFile()

  function handleRemoveFile(fileId: string) {
    if (!portInfoId) return
    deleteFile(
      { fileId, portInfoId },
      {
        onSuccess: () => {
          setStatus('File deleted', undefined, 'success')
        },
        onError: () => {
          setStatus('Failed to delete file', undefined, 'error')
        },
      },
    )
  }

  function handleFilesUpload(fieldKey: string, filesToUpload: File[]) {
    if (!portInfoId) {
      setStatus('Cannot upload file: visit not saved yet', undefined, 'error')
      return
    }

    let successCount = 0
    let errorCount = 0
    const totalFiles = filesToUpload.length

    filesToUpload.forEach((file) => {
      uploadFile(
        {
          portInfoId,
          field: fieldKey,
          file,
          title: file.name,
        },
        {
          onSuccess: () => {
            successCount++
            if (successCount + errorCount === totalFiles) {
              if (errorCount === 0) {
                setStatus(
                  totalFiles === 1
                    ? 'File uploaded successfully'
                    : `${successCount} files uploaded successfully`,
                  undefined,
                  'success',
                )
              } else {
                setStatus(
                  `${successCount} uploaded, ${errorCount} failed`,
                  undefined,
                  'error',
                )
              }
            }
          },
          onError: () => {
            errorCount++
            if (successCount + errorCount === totalFiles) {
              setStatus(
                `${successCount} uploaded, ${errorCount} failed`,
                undefined,
                'error',
              )
            }
          },
        },
      )
    })
  }
  function handleFileSizeError(rejectedFiles: File[]) {
    const names = rejectedFiles.map((f) => f.name).join(', ')
    setStatus(
      `File(s) too large (max ${MAX_FILE_SIZE_LABEL}): ${names}`,
      undefined,
      'error',
    )
  }
  return (
    <Box sx={{ maxHeight: 'calc(80vh - 140px)', overflowY: 'auto', pr: 1 }}>
      <Box sx={sectionBoxSx}>
        <Typography variant="paragraphBold2">Visit Information</Typography>
        <Box sx={fieldsContainerSx}>
          <PortAutocomplete
            value={formData?.port}
            onOptionChange={(port) =>
              onChangeHandler({ key: 'port', value: port })
            }
            error={missingFields.includes('port')}
          />

          <TextField
            size="small"
            sx={{ width: inputWidth }}
            label="GISIS Code"
            autoComplete="off"
            value={formData?.gisisCode || ''}
            onChange={(e) =>
              onChangeHandler({ key: 'gisisCode', value: e.target.value })
            }
          />

          <Box>
            <PortInfoFieldAutocomplete
              fieldType="terminal"
              value={formData?.terminalName}
              onChange={(terminalName) =>
                onChangeHandler({ key: 'terminalName', value: terminalName })
              }
              error={missingFields.includes('terminalName')}
              disabled={formData?.terminalName === FALLBACK_STRING_NA}
            />
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={formData?.terminalName === FALLBACK_STRING_NA}
                  onChange={(e) => {
                    if (e.target.checked) {
                      onChangeHandler({
                        key: 'terminalName',
                        value: FALLBACK_STRING_NA,
                      })
                    } else {
                      onChangeHandler({ key: 'terminalName', value: undefined })
                    }
                  }}
                />
              }
              label="Not Applicable"
              sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
            />
          </Box>

          <PortInfoFieldAutocomplete
            fieldType="berth"
            value={formData?.berthName}
            onChange={(berthName) =>
              onChangeHandler({ key: 'berthName', value: berthName })
            }
            error={missingFields.includes('berthName')}
          />

          <VesselAutocomplete
            vesselName={formData?.vesselName}
            imoNumber={formData?.imoNumber}
            onVesselChange={handleVesselChange}
            error={
              missingFields.includes('vesselName') ||
              missingFields.includes('imoNumber')
            }
          />

          <FormControl size="small" sx={{ width: inputWidth }} required>
            <InputLabel id="pool-select-label-page2">Pool/Segment</InputLabel>
            <Select
              labelId="pool-select-label-page2"
              label="Pool/Segment"
              value={formData?.pool || ''}
              onChange={(e) =>
                onChangeHandler({
                  key: 'pool',
                  value: e.target.value || undefined,
                })
              }
              MenuProps={{ disablePortal: true }}
            >
              {pools.map((pool) => (
                <MenuItem key={pool.value} value={pool.value}>
                  {pool.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box sx={sectionBoxSx}>
        <Typography variant="paragraphBold2">Voyage Details</Typography>
        <Box sx={fieldsContainerSx}>
          <DatePicker
            slots={{
              openPickerIcon: CalendarIcon,
            }}
            format={dateFormat}
            slotProps={{
              textField: {
                size: 'small',
                error: missingFields.includes('dateOfArrival'),
              },
              popper: {
                disablePortal: true,
              },
            }}
            sx={{ width: inputWidth }}
            value={formData?.dateOfArrival || null}
            label="Date of Arrival *"
            onChange={(date) => {
              if (date) {
                onChangeHandler({ key: 'dateOfArrival', value: date })
              }
            }}
          />

          <DatePicker
            slots={{
              openPickerIcon: CalendarIcon,
            }}
            format={dateFormat}
            slotProps={{
              textField: {
                size: 'small',
                required: true,
                error: missingFields.includes('dateOfDeparture'),
              },
              popper: {
                disablePortal: true,
              },
            }}
            sx={{ width: inputWidth }}
            value={formData?.dateOfDeparture || null}
            label="Date of Departure"
            onChange={(date) => {
              onChangeHandler({
                key: 'dateOfDeparture',
                value: date || undefined,
              })
            }}
          />

          <FormControl
            size="small"
            sx={{ width: inputWidth }}
            required
            error={missingFields.includes('operation')}
          >
            <InputLabel id="operation-select-label">Operation</InputLabel>
            <Select
              labelId="operation-select-label"
              label="Operation"
              value={formData?.operation || ''}
              onChange={(e) =>
                onChangeHandler({
                  key: 'operation',
                  value: e.target.value as PortInfoOperation,
                })
              }
              MenuProps={{ disablePortal: true }}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {operationOptions.map((op) => (
                <MenuItem key={op} value={op}>
                  {op}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl
            size="small"
            sx={{ width: inputWidth }}
            required
            error={missingFields.includes('cargoType')}
          >
            <InputLabel>Cargo Type</InputLabel>
            <Select
              label="Cargo Type"
              value={formData?.cargoType || ''}
              onChange={(e) =>
                onChangeHandler({ key: 'cargoType', value: e.target.value })
              }
              MenuProps={{ disablePortal: true }}
            >
              {cargoTypeOptions.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box sx={sectionBoxSx}>
        <Typography variant="paragraphBold2">
          Draft Information Related to this Visit
        </Typography>
        <Box sx={fieldsContainerSx}>
          <TextField
            size="small"
            sx={numberInputSx}
            label="Arrival Draft (m)"
            required
            type="number"
            autoComplete="off"
            error={missingFields.includes('arrivalDraft')}
            value={formData?.arrivalDraft ?? ''}
            onChange={(e) => handleNumberChange('arrivalDraft', e.target.value)}
            inputProps={{ step: '0.01' }}
          />

          <TextField
            size="small"
            sx={numberInputSx}
            label="Departure Draft (m)"
            required
            type="number"
            autoComplete="off"
            error={missingFields.includes('departureDraft')}
            value={formData?.departureDraft ?? ''}
            onChange={(e) =>
              handleNumberChange('departureDraft', e.target.value)
            }
            inputProps={{ step: '0.01' }}
          />

          <TextField
            size="small"
            sx={numberInputSx}
            label="Air Draft (m)"
            type="number"
            autoComplete="off"
            error={missingFields.includes('airDraft')}
            value={formData?.airDraft ?? ''}
            onChange={(e) => handleNumberChange('airDraft', e.target.value)}
            inputProps={{ step: '0.01' }}
          />
        </Box>
      </Box>

      <Box sx={sectionBoxSx}>
        <Typography variant="paragraphBold2">Cargo Operations</Typography>
        <Box sx={fieldsContainerSx}>
          <FormControl
            size="small"
            sx={{ width: inputWidth }}
            required
            error={missingFields.includes('ispsSecurityLevel')}
          >
            <InputLabel>ISPS Security Level</InputLabel>
            <Select
              label="ISPS Security Level"
              value={formData?.ispsSecurityLevel || ''}
              onChange={(e) =>
                onChangeHandler({
                  key: 'ispsSecurityLevel',
                  value: e.target.value as PortInfoIspsSecurityLevel,
                })
              }
              MenuProps={{ disablePortal: true }}
            >
              {ispsSecurityLevelOptions.map((level) => (
                <MenuItem key={level} value={level}>
                  {level}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            size="small"
            sx={{ width: inputWidth }}
            label="Cargo Connections"
            required
            autoComplete="off"
            error={missingFields.includes('cargoConnections')}
            value={formData?.cargoConnections || ''}
            onChange={(e) =>
              onChangeHandler({
                key: 'cargoConnections',
                value: e.target.value,
              })
            }
          />

          <TextField
            size="small"
            sx={numberInputSx}
            label="Manifold Pressure (bar)"
            required
            type="number"
            autoComplete="off"
            error={missingFields.includes('manifoldPressure')}
            value={formData?.manifoldPressure ?? ''}
            onChange={(e) =>
              handleNumberChange('manifoldPressure', e.target.value)
            }
            inputProps={{ step: '0.1' }}
          />

          <TextField
            size="small"
            sx={numberInputSx}
            label="Load/Discharge Rate (m³/hr)"
            type="number"
            autoComplete="off"
            value={formData?.loadDischargeRate ?? ''}
            onChange={(e) =>
              handleNumberChange('loadDischargeRate', e.target.value)
            }
            inputProps={{ step: '1' }}
          />
        </Box>
      </Box>

      <Box sx={sectionBoxSx}>
        <Typography variant="paragraphBold2">Port Services</Typography>
        <Box sx={fieldsContainerSx}>
          <TextField
            size="small"
            sx={{ width: inputWidth }}
            label="Fresh Water Available"
            autoComplete="off"
            value={formData?.freshWaterAvailable || ''}
            onChange={(e) =>
              onChangeHandler({
                key: 'freshWaterAvailable',
                value: e.target.value,
              })
            }
          />

          <TextField
            size="small"
            sx={{ width: inputWidth }}
            label="Garbage Landing Available"
            autoComplete="off"
            value={formData?.garbageLandingAvailable || ''}
            onChange={(e) =>
              onChangeHandler({
                key: 'garbageLandingAvailable',
                value: e.target.value,
              })
            }
          />

          <TextField
            size="small"
            sx={{ width: inputWidth }}
            label="Slops/Sludge Landing Available"
            autoComplete="off"
            value={formData?.slopsSludgeLandingAvailable || ''}
            onChange={(e) =>
              onChangeHandler({
                key: 'slopsSludgeLandingAvailable',
                value: e.target.value,
              })
            }
          />

          <TextField
            size="small"
            sx={{ width: inputWidth }}
            label="De-Slopping Available (price/m3)"
            autoComplete="off"
            value={formData?.deSloppingAvailable || ''}
            onChange={(e) =>
              onChangeHandler({
                key: 'deSloppingAvailable',
                value: e.target.value,
              })
            }
          />

          <TextField
            size="small"
            sx={numberInputSx}
            label="Dock Water Density"
            type="number"
            autoComplete="off"
            value={formData?.dockWaterDensity ?? ''}
            onChange={(e) =>
              handleNumberChange('dockWaterDensity', e.target.value)
            }
            inputProps={{ step: '0.001' }}
          />
        </Box>
      </Box>

      <Box sx={sectionBoxSx}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="paragraphBold2">General Remarks</Typography>
          <Typography
            variant="caption"
            sx={{
              color: COLORS.greys.dark,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <ClipIcon style={{ fontSize: '0.875rem' }} />
            Drag & drop files (max {MAX_FILE_SIZE_LABEL} per file) onto any
            remark, or click "Attach"
          </Typography>
        </Box>
        <Box sx={remarksGridSx}>
          {REMARK_FIELDS.map(({ label, key }) => (
            <RemarkFieldWithAttach
              key={key}
              label={label}
              value={(formData?.[key] as string) || ''}
              onChange={(value) => onChangeHandler({ key, value })}
              onFilesSelect={(files) => handleFilesUpload(key, files)}
              onFileSizeError={handleFileSizeError}
              attachedFiles={getFilesForField(files, key)}
              onRemoveFile={handleRemoveFile}
              isUploading={isUploading}
            />
          ))}
        </Box>
      </Box>
    </Box>
  )
}
